// src/lib/partner/partnerCohorts.js

import { supabase } from "../../supabaseClient";


const VALID_STATUSES = [
  "draft",
  "active",
  "completed",
  "archived",
];


export const PROGRAMME_TYPE_OPTIONS = [
  {
    value: "incubator",
    label: "Incubator",
  },
  {
    value: "accelerator",
    label: "Accelerator",
  },
  {
    value: "supplier",
    label: "Supplier",
  },
  {
    value: "development",
    label: "Development",
  },
  {
    value: "other",
    label: "Other",
  },
];


const VALID_PROGRAMME_TYPES =
  PROGRAMME_TYPE_OPTIONS.map(
    (option) =>
      option.value
  );


/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function normaliseRequiredText(
  value,
  label
) {
  const clean =
    String(value || "").trim();


  if (!clean) {
    throw new Error(
      `${label} is required.`
    );
  }


  return clean;
}


function normaliseOptionalDate(
  value
) {
  const clean =
    String(value || "").trim();


  return clean || null;
}


function normaliseCapacity(
  value
) {
  if (
    value === null ||
    value === undefined ||
    String(value).trim() === ""
  ) {
    return null;
  }


  const capacity =
    Number(value);


  if (
    !Number.isInteger(
      capacity
    ) ||
    capacity <= 0
  ) {
    throw new Error(
      "Cohort size must be a whole number greater than zero."
    );
  }


  return capacity;
}


function normaliseProgrammeType(
  value
) {
  const programmeType =
    String(value || "")
      .trim()
      .toLowerCase();


  if (
    !VALID_PROGRAMME_TYPES.includes(
      programmeType
    )
  ) {
    throw new Error(
      "Please select a valid programme type."
    );
  }


  return programmeType;
}


function normaliseStatus(
  value
) {
  const status =
    String(value || "")
      .trim()
      .toLowerCase();


  if (
    !VALID_STATUSES.includes(
      status
    )
  ) {
    throw new Error(
      "Please select a valid cohort status."
    );
  }


  return status;
}


function normaliseNullableNumber(
  value
) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }


  const number =
    Number(value);


  return Number.isFinite(
    number
  )
    ? number
    : null;
}


function validateDates(
  startDate,
  endDate
) {
  if (
    startDate &&
    endDate &&
    endDate < startDate
  ) {
    throw new Error(
      "The cohort end date cannot be before the start date."
    );
  }
}


/* -------------------------------------------------------------------------- */
/* Read                                                                       */
/* -------------------------------------------------------------------------- */

export async function fetchPartnerCohorts() {
  const {
    data,
    error,
  } = await supabase.rpc(
    "get_my_partner_cohorts"
  );


  if (error) {
    throw error;
  }


  return Array.isArray(data)
    ? data
    : [];
}


export async function fetchPartnerProgrammeAccess(
  partnerId
) {
  if (!partnerId) {
    throw new Error(
      "The partner organisation could not be identified."
    );
  }


  const {
    data,
    error,
  } = await supabase.rpc(
    "get_partner_programme_access",
    {
      p_partner_id:
        partnerId,
    }
  );


  if (error) {
    throw error;
  }


  const result =
    data || {};


  return {
    ...result,

    configured:
      result.configured === true,

    status:
      result.status || null,

    effective_status:
      result.effective_status ||
      "not_configured",

    max_cohorts:
      normaliseNullableNumber(
        result.max_cohorts
      ),

    cohort_usage:
      Number(
        result.cohort_usage || 0
      ),

    cohorts_remaining:
      normaliseNullableNumber(
        result.cohorts_remaining
      ),

    can_manage_cohorts:
      result.can_manage_cohorts ===
      true,

    can_create_cohort:
      result.can_create_cohort ===
      true,

    creation_block_code:
      result.creation_block_code ||
      null,
  };
}


export async function fetchPartnerCohortDetail(
  cohortId
) {
  if (!cohortId) {
    throw new Error(
      "The cohort could not be identified."
    );
  }


  const {
    data,
    error,
  } = await supabase.rpc(
    "get_partner_cohort_detail_v2",
    {
      p_cohort_id:
        cohortId,
    }
  );


  if (error) {
    throw error;
  }


  const cohort =
    Array.isArray(data)
      ? data[0]
      : data;


  if (!cohort) {
    throw new Error(
      "The cohort could not be found or you do not have access to it."
    );
  }


  return cohort;
}


/* -------------------------------------------------------------------------- */
/* Create                                                                     */
/* -------------------------------------------------------------------------- */

export async function createPartnerCohort({
  partnerId,
  name,
  programmeType,
  startDate,
  endDate,
  capacity,
}) {
  const cleanName =
    normaliseRequiredText(
      name,
      "Cohort name"
    );


  const cleanProgrammeType =
    normaliseProgrammeType(
      programmeType
    );


  const cleanStartDate =
    normaliseOptionalDate(
      startDate
    );


  const cleanEndDate =
    normaliseOptionalDate(
      endDate
    );


  if (!partnerId) {
    throw new Error(
      "The partner organisation could not be identified."
    );
  }


  validateDates(
    cleanStartDate,
    cleanEndDate
  );


  const {
    data,
    error,
  } = await supabase.rpc(
    "create_partner_cohort",
    {
      p_partner_id:
        partnerId,

      p_name:
        cleanName,

      p_programme_type:
        cleanProgrammeType,

      p_start_date:
        cleanStartDate,

      p_end_date:
        cleanEndDate,

      p_capacity:
        normaliseCapacity(
          capacity
        ),
    }
  );


  if (error) {
    throw error;
  }


  return data;
}


/* -------------------------------------------------------------------------- */
/* Update                                                                     */
/* -------------------------------------------------------------------------- */

export async function updatePartnerCohort({
  cohortId,
  name,
  programmeType,
  startDate,
  endDate,
  capacity,
  status,
}) {
  const cleanName =
    normaliseRequiredText(
      name,
      "Cohort name"
    );


  const cleanProgrammeType =
    normaliseProgrammeType(
      programmeType
    );


  const cleanStartDate =
    normaliseOptionalDate(
      startDate
    );


  const cleanEndDate =
    normaliseOptionalDate(
      endDate
    );


  if (!cohortId) {
    throw new Error(
      "The cohort could not be identified."
    );
  }


  validateDates(
    cleanStartDate,
    cleanEndDate
  );


  const {
    data,
    error,
  } = await supabase.rpc(
    "update_partner_cohort_v2",
    {
      p_cohort_id:
        cohortId,

      p_name:
        cleanName,

      p_programme_type:
        cleanProgrammeType,

      p_start_date:
        cleanStartDate,

      p_end_date:
        cleanEndDate,

      p_capacity:
        normaliseCapacity(
          capacity
        ),

      p_status:
        normaliseStatus(
          status
        ),
    }
  );


  if (error) {
    throw error;
  }


  return data;
}


/* -------------------------------------------------------------------------- */
/* Archive                                                                    */
/* -------------------------------------------------------------------------- */

export async function archivePartnerCohort(
  cohortId
) {
  if (!cohortId) {
    throw new Error(
      "The cohort could not be identified."
    );
  }


  const {
    data,
    error,
  } = await supabase.rpc(
    "archive_partner_cohort",
    {
      p_cohort_id:
        cohortId,
    }
  );


  if (error) {
    throw error;
  }


  return data === true;
}